/**
 * BugReportDialog — Dialog compact pour signaler un bug sur un exercice.
 * Pré-remplit les métadonnées automatiquement.
 */

import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import { Loader2 } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { createBugReport } from "../../lib/adminApi";

const CATEGORIES = [
  { value: "affichage", label: "Affichage" },
  { value: "reponse_fausse", label: "Réponse fausse" },
  { value: "fallback", label: "Fallback" },
  { value: "generation_error", label: "Erreur de génération" },
  { value: "autre", label: "Autre" },
];

const PRIORITIES = [
  { value: "low", label: "Basse", color: "bg-gray-500" },
  { value: "medium", label: "Moyenne", color: "bg-yellow-500" },
  { value: "high", label: "Haute", color: "bg-orange-500" },
  { value: "critical", label: "Critique", color: "bg-red-500" },
];

export default function BugReportDialog({ open, onOpenChange, exercise, chapterCode, objectif }) {
  const { toast } = useToast();
  const [category, setCategory] = useState("affichage");
  const [priority, setPriority] = useState("medium");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!exercise) return null;

  const meta = exercise.metadata || {};

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Les champs de contexte partent en TEXTE, quoi qu'ils vaillent ici.
      //
      // Un générateur renvoyait le niveau `6` au lieu de « 6e » : le rapport
      // entier était refusé, et la personne perdait son signalement sur un
      // « Erreur de validation des paramètres » qu'elle ne pouvait pas
      // comprendre. Le serveur convertit désormais lui aussi ; on ne compte
      // pas sur un seul des deux.
      const texte = (v) =>
        v === null || v === undefined || v === "" ? null : String(v);

      const payload = {
        exercise_id: texte(exercise.id_exercice || exercise.exercise_uid) || "",
        generator_key: texte(meta.generator_key || exercise.generator_key),
        generator_version: texte(meta.generator_version || meta.version),
        seed: meta.seed ?? meta.seed_used ?? meta.variables?.seed ?? exercise.generation_meta?.seed ?? null,
        blueprint_id: texte(meta.blueprint_id_used),
        difficulty: texte(meta.difficulty_effective || meta.difficulty || exercise.difficulty || meta.preset_used),
        grade: texte(meta.grade_effective || exercise.niveau),
        chapter_code: chapterCode || null,
        exercise_params: meta.variables || exercise.variables || {},
        metadata_snapshot: objectif ? { ...meta, objectif_h: objectif } : meta,
        enonce_html: exercise.enonce_html || null,
        solution_html: exercise.solution_html || null,
        category,
        priority,
        comment: comment.trim() || null,
      };

      const result = await createBugReport(payload);

      if (result.success) {
        // L'accusé de réception porte le NUMÉRO. Sans lui, la personne ne sait
        // pas si son signalement est parti, et ne recommence pas.
        toast({
          title: `Signalement ${result.data.ticket_id} enregistré`,
          description: result.data.message
            || "Le tirage exact est joint : le problème pourra être rejoué à l'identique.",
        });
        // Reset form
        setCategory("affichage");
        setPriority("medium");
        setComment("");
        onOpenChange(false);
      } else {
        toast({
          title: "Erreur",
          description: result.error || "Le signalement n'a pas pu être envoyé.",
          variant: "destructive",
        });
      }
    } catch (err) {
      console.error("[RETOURS] signalement impossible :", err);
      toast({
        title: "Erreur",
        description: "Le signalement n'a pas pu être envoyé.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-dialog sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Signaler un problème</DialogTitle>
          <DialogDescription>
            Le générateur, sa version et la graine du tirage sont joints
            automatiquement : le problème pourra être rejoué à l'identique.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Read-only metadata */}
          <details className="overflow-auto bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-xs space-y-1">
            <summary className="min-h-11 cursor-pointer py-3">Informations de reproduction jointes</summary>
            {meta.generator_key && (
              <div className="flex justify-between">
                <span className="text-gray-500">Générateur</span>
                <Badge variant="outline" className="text-xs">{meta.generator_key}</Badge>
              </div>
            )}
            {exercise.id_exercice && (
              <div className="flex justify-between">
                <span className="text-gray-500">Exercise ID</span>
                <span className="font-mono">{exercise.id_exercice.slice(0, 30)}</span>
              </div>
            )}
            {(meta.seed ?? meta.seed_used ?? meta.variables?.seed ?? exercise.generation_meta?.seed) != null && (
              <div className="flex justify-between">
                <span className="text-gray-500">Seed</span>
                <span className="font-mono">{meta.seed ?? meta.seed_used ?? meta.variables?.seed ?? exercise.generation_meta?.seed}</span>
              </div>
            )}
            {meta.blueprint_id_used && (
              <div className="flex justify-between">
                <span className="text-gray-500">Blueprint</span>
                <span className="font-mono">{meta.blueprint_id_used}</span>
              </div>
            )}
            {meta.difficulty_effective && (
              <div className="flex justify-between">
                <span className="text-gray-500">Difficulté</span>
                <span>{meta.difficulty_effective}</span>
              </div>
            )}
            {exercise.niveau && (
              <div className="flex justify-between">
                <span className="text-gray-500">Niveau</span>
                <span>{exercise.niveau}</span>
              </div>
            )}
          </details>

          {/* Category */}
          <div className="space-y-2">
            <Label>Catégorie</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label>Priorité</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Comment */}
          <div className="space-y-2">
            <Label>Commentaire (optionnel)</Label>
            <Textarea
              placeholder="Décrivez le problème..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
            />
          </div>

          {/* Submit */}
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-red-600 hover:bg-red-700 text-white"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Envoi...
              </>
            ) : (
              "Signaler le bug"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
